const raw = {
  apiUrl: (import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api').replace(/\/$/, ''),
  rootDomain: (import.meta.env.VITE_ROOT_DOMAIN ?? '').trim().toLowerCase(),
  appSubdomain: (import.meta.env.VITE_APP_SUBDOMAIN ?? 'app').trim().toLowerCase(),
  webUrl: (import.meta.env.VITE_WEB_URL ?? '').replace(/\/$/, ''),
};

export const config = {
  apiUrl: raw.apiUrl,
  /** Empty in local dev; set to e.g. "portfilo.online" in prod for `<sub>.portfilo.online`. */
  rootDomain: raw.rootDomain,
  /** The subdomain the builder app itself runs on (e.g. "app" → app.portfilo.online). */
  appSubdomain: raw.appSubdomain,
} as const;

/**
 * Public URL for a portfolio by subdomain.
 * - Prod (rootDomain set): `https://<sub>.<rootDomain>`
 * - Local dev: `<origin>/p/<sub>` so you can preview without real DNS.
 */
export function publicPortfolioUrl(subdomain: string): string {
  if (config.rootDomain) {
    return `https://${subdomain}.${config.rootDomain}`;
  }
  const origin = raw.webUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/p/${subdomain}`;
}

/** Human-readable label for the subdomain (for UI chips, toasts, etc). */
export function portfolioLabel(subdomain: string): string {
  if (config.rootDomain) return `${subdomain}.${config.rootDomain}`;
  return `/p/${subdomain}`;
}

/**
 * If the current browser hostname is a published-portfolio host (tenant subdomain
 * of rootDomain, or a custom domain), return the full hostname to send to the
 * backend's `/public/config?host=` endpoint. Returns `null` when we're on the
 * builder app itself (app.<rootDomain>, <rootDomain>, www.<rootDomain>, or any
 * localhost/loopback origin used in dev).
 */
export function publicHostFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.trim().toLowerCase();
  if (!host) return null;

  // Dev / loopback is always the app.
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.localhost')
  ) {
    return null;
  }

  const root = config.rootDomain;
  if (root) {
    if (host === root || host === `www.${root}`) return null;
    if (host === `${config.appSubdomain}.${root}`) return null;
    if (host.endsWith(`.${root}`)) {
      // A tenant subdomain of rootDomain — send the whole host; backend strips suffix.
      return host;
    }
  }

  // Any other host is treated as a custom domain pointed at our frontend.
  return host;
}
